import React, { useRef, useState } from 'react'
import { Eye, Pencil, Info, Download, X, Paperclip, ChevronDown, ChevronUp } from 'lucide-react'
import {
  BASE_CHECKLIST_DOCUMENTS,
  ChecklistDocumentItem,
  DocumentStatus,
  DocumentTypeKey,
  UserFileAttachment,
} from '../../types/userDocumentTypes'
import AttachmentApiService, { GalleryFile } from '../../services/AttachmentApiService'
import AttachmentSourceModal from '../AttachmentSourceModal'
import AttachmentGalleryModal from '../AttachmentGalleryModal'
import UserAttachmentSection, { AttachmentData } from '../UserAttachmentSection'
import { formatDate } from '../../utils/masks'
import openSignedAttachment from '../../utils/openSignedAttachment'
import { downloadRemoteFile } from '../../utils/downloadRemoteFile'
import {
  ChecklistContainer,
  ChecklistRow,
  ChecklistRowItemWrapper,
  DocActions,
  DocDropdownContent,
  DocDropdownRow,
  DocFileInfo,
  DocFileItem,
  DocFileName,
  DocFileSize,
  DocLabel,
  ExpirationInput,
  IconButton,
  SectionHeader,
  StatusTag,
  TabWrapper,
  WarningBanner,
} from './style'

interface UserDocumentsTabProps {
  attachments: AttachmentData[]
  setAttachments: React.Dispatch<React.SetStateAction<AttachmentData[]>>
  userRegime?: string
  disabled?: boolean
}

function formatSize(bytes?: number): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

const UserDocumentsTab: React.FC<UserDocumentsTabProps> = ({
  attachments,
  setAttachments,
  userRegime = 'CLT',
  disabled = false,
}) => {
  const [activeUploadKey, setActiveUploadKey] = useState<DocumentTypeKey | null>(null)
  const [openDropdownKeys, setOpenDropdownKeys] = useState<Set<DocumentTypeKey>>(new Set())
  const [isSourceChoiceOpen, setIsSourceChoiceOpen] = useState(false)
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)
  const checklistFileInputRef = useRef<HTMLInputElement>(null)

  // Map attachments to include documentType
  const typedAttachments: UserFileAttachment[] = attachments.map((a) => ({
    id: a.id,
    file: a.file,
    fileUrl: a.fileUrl,
    title: a.title,
    documentType: (a as any).documentType || 'GERAL',
    expirationDate: (a as any).expirationDate || '',
    status: (a as any).status || 'ENTREGUE',
    isEditing: a.isEditing,
  }))

  // Filter general attachments ONLY for the bottom section
  const generalAttachments = attachments.filter((a) => {
    const dt = (a as any).documentType
    return !dt || dt === 'GERAL'
  })

  const setGeneralAttachments = (
    action: React.SetStateAction<AttachmentData[]>,
  ) => {
    setAttachments((prev) => {
      const checklistItems = prev.filter((a) => {
        const dt = (a as any).documentType
        return dt && dt !== 'GERAL'
      })
      const currentGeneral = prev.filter((a) => {
        const dt = (a as any).documentType
        return !dt || dt === 'GERAL'
      })
      const updatedGeneral =
        typeof action === 'function' ? action(currentGeneral) : action

      // Ensure new general files carry documentType: 'GERAL'
      const markedGeneral = updatedGeneral.map((g) => ({
        ...g,
        documentType: (g as any).documentType || 'GERAL',
      }))

      return [...checklistItems, ...markedGeneral]
    })
  }

  const isDateExpired = (expDateStr?: string): boolean => {
    if (!expDateStr) return false
    try {
      let dateObj: Date | null = null
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(expDateStr)) {
        const [d, m, y] = expDateStr.split('/')
        dateObj = new Date(Number(y), Number(m) - 1, Number(d), 23, 59, 59)
      } else if (/^\d{2}\/\d{4}$/.test(expDateStr)) {
        const [m, y] = expDateStr.split('/')
        dateObj = new Date(Number(y), Number(m), 0, 23, 59, 59)
      } else if (expDateStr.includes('T') || expDateStr.includes('-')) {
        dateObj = new Date(expDateStr)
      }

      if (dateObj && !isNaN(dateObj.getTime())) {
        return dateObj < new Date()
      }
    } catch {
      return false
    }
    return false
  }

  const getDocumentStatus = (doc: ChecklistDocumentItem): DocumentStatus => {
    const docFiles = typedAttachments.filter((a) => a.documentType === doc.key)
    if (docFiles.length > 0) {
      const hasExpired = docFiles.some((a) => isDateExpired(a.expirationDate))
      if (hasExpired) return 'VENCIDO'
      return 'ENTREGUE'
    }
    return 'PENDENTE'
  }

  const toggleDropdown = (docKey: DocumentTypeKey) => {
    setOpenDropdownKeys((prev) => {
      const next = new Set(prev)
      if (next.has(docKey)) next.delete(docKey)
      else next.add(docKey)
      return next
    })
  }

  const handleChecklistUploadClick = (docKey: DocumentTypeKey) => {
    if (disabled) return
    setActiveUploadKey(docKey)
    setIsSourceChoiceOpen(true)
  }

  const handleSelectGalleryFilesForChecklist = async (galleryFiles: GalleryFile[]) => {
    if (!activeUploadKey) return
    const key = activeUploadKey
    const converted = await Promise.all(
      galleryFiles.map(async (gf) => {
        try {
          const signedUrl = gf.signedUrl || (await AttachmentApiService.getSignedUrl(gf.fileUrl))
          const res = await fetch(signedUrl)
          const blob = await res.blob()
          const file = new File([blob], gf.originalName || gf.fileName || gf.title || 'anexo', {
            type: gf.mimeType || blob.type,
          })
          return {
            file,
            fileUrl: gf.fileUrl,
            title: gf.title || gf.originalName || gf.fileName,
            isEditing: false,
            documentType: key,
            status: 'ENTREGUE' as const,
          }
        } catch {
          return {
            fileUrl: gf.fileUrl,
            title: gf.title || gf.originalName || gf.fileName,
            isEditing: false,
            documentType: key,
            status: 'ENTREGUE' as const,
          }
        }
      }),
    )
    setAttachments((prev) => [...prev, ...converted])
    setOpenDropdownKeys((prev) => new Set(prev).add(key))
    setActiveUploadKey(null)
  }

  const handleChecklistFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files
    if (!fileList || fileList.length === 0 || !activeUploadKey) return

    const newItems: (AttachmentData & { documentType: DocumentTypeKey; status: DocumentStatus })[] =
      Array.from(fileList).map((file) => ({
        file,
        title: file.name,
        isEditing: false,
        documentType: activeUploadKey,
        status: 'ENTREGUE',
      }))

    setAttachments((prev) => [...prev, ...newItems])

    setOpenDropdownKeys((prev) => new Set(prev).add(activeUploadKey))
    setActiveUploadKey(null)
    if (checklistFileInputRef.current) checklistFileInputRef.current.value = ''
  }

  const handleViewDocument = (docKey: DocumentTypeKey) => {
    const found = typedAttachments.find((a) => a.documentType === docKey)
    if (!found) return
    if (found.fileUrl) {
      void openSignedAttachment(found.fileUrl)
    } else if (found.file) {
      const url = URL.createObjectURL(found.file)
      window.open(url, '_blank')
    }
  }

  const handleDownloadFile = (a: UserFileAttachment) => {
    if (a.fileUrl) {
      void downloadRemoteFile(a.fileUrl, a.title || 'documento')
    } else if (a.file) {
      const url = URL.createObjectURL(a.file)
      const link = document.createElement('a')
      link.href = url
      link.download = a.file.name || a.title || 'documento'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }
  }

  const handleViewSingleFile = (item: UserFileAttachment) => {
    if (item.fileUrl) {
      void openSignedAttachment(item.fileUrl)
    } else if (item.file) {
      const url = URL.createObjectURL(item.file)
      window.open(url, '_blank')
    }
  }

  const handleExpirationDateChange = (
    fileItem: UserFileAttachment,
    rawVal: string,
  ) => {
    const formatted = formatDate(rawVal)
    setAttachments((prev) =>
      prev.map((a) => {
        const isTarget =
          (fileItem.id && a.id === fileItem.id) ||
          (fileItem.file && a.file === fileItem.file) ||
          a.title === fileItem.title
        if (isTarget) {
          return {
            ...a,
            expirationDate: formatted,
          } as any
        }
        return a
      }),
    )
  }

  const handleRemoveChecklistFile = (fileItem: UserFileAttachment) => {
    if (disabled) return
    setAttachments((prev) =>
      prev.filter((a) => {
        if (fileItem.id && a.id) return a.id !== fileItem.id
        if (fileItem.file && a.file) return a.file !== fileItem.file
        return a.title !== fileItem.title
      }),
    )
  }

  const pendingCount = BASE_CHECKLIST_DOCUMENTS.filter(
    (doc) => getDocumentStatus(doc) === 'PENDENTE',
  ).length

  const expiredCount = BASE_CHECKLIST_DOCUMENTS.filter(
    (doc) => getDocumentStatus(doc) === 'VENCIDO',
  ).length

  return (
    <TabWrapper>
      {/* Hidden File Input for Checklist Items */}
      <input
        type="file"
        ref={checklistFileInputRef}
        multiple
        style={{ display: 'none' }}
        onChange={handleChecklistFileChange}
      />

      {/* BLOCO 1: CHECKLIST — DOCUMENTAÇÃO BÁSICA */}
      <div>
        <SectionHeader>
          CHECKLIST — DOCUMENTAÇÃO BÁSICA
          <Info size={14} color="#9CA3AF" />
        </SectionHeader>

        <ChecklistContainer>
          {BASE_CHECKLIST_DOCUMENTS.map((doc) => {
            const status = getDocumentStatus(doc)
            const docFiles = typedAttachments.filter((a) => a.documentType === doc.key)
            const hasFile = docFiles.length > 0
            const isOpen = openDropdownKeys.has(doc.key)

            return (
              <ChecklistRowItemWrapper key={doc.key}>
                <ChecklistRow>
                  <DocLabel>{doc.label}</DocLabel>
                  <DocActions>
                    <StatusTag $status={status}>
                      {status === 'ENTREGUE' ? '✓ ENTREGUE' : status}
                    </StatusTag>

                    {hasFile && (
                      <IconButton
                        type="button"
                        title={isOpen ? 'Recolher detalhes' : 'Expandir detalhes do documento'}
                        onClick={() => toggleDropdown(doc.key)}
                      >
                        {isOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                      </IconButton>
                    )}

                    {hasFile && (
                      <IconButton
                        type="button"
                        title="Visualizar anexo"
                        onClick={() => handleViewSingleFile(docFiles[0])}
                      >
                        <Eye size={15} />
                      </IconButton>
                    )}

                    {!disabled && (
                      <IconButton
                        type="button"
                        title="Adicionar ou alterar anexo"
                        onClick={() => handleChecklistUploadClick(doc.key)}
                      >
                        <Pencil size={16} />
                      </IconButton>
                    )}
                  </DocActions>
                </ChecklistRow>

                {/* Dropdown expansível com arquivo(s) anexados daquela linha */}
                {isOpen && hasFile && (
                  <DocDropdownContent>
                    {docFiles.map((item, idx) => (
                      <DocDropdownRow key={item.id || idx}>
                        <DocFileItem>
                          <DocFileInfo>
                            <Paperclip size={13} color="#6B7280" />
                            <DocFileName>{item.title || item.file?.name || 'Documento'}</DocFileName>
                            {item.file?.size && (
                              <DocFileSize>({formatSize(item.file.size)})</DocFileSize>
                            )}
                          </DocFileInfo>

                          <DocActions>
                            <IconButton
                              type="button"
                              title="Visualizar este anexo"
                              onClick={() => handleViewSingleFile(item)}
                            >
                              <Eye size={13} />
                            </IconButton>

                            <IconButton
                              type="button"
                              title="Baixar este anexo"
                              onClick={() => handleDownloadFile(item)}
                            >
                              <Download size={13} />
                            </IconButton>

                            {!disabled && (
                              <IconButton
                                type="button"
                                title="Remover este anexo"
                                onClick={() => handleRemoveChecklistFile(item)}
                              >
                                <X size={13} color="#EF4444" />
                              </IconButton>
                            )}
                          </DocActions>
                        </DocFileItem>

                        <ExpirationInput
                          type="text"
                          placeholder="Validade (DD/MM/AAAA)"
                          value={(item as any).expirationDate || ''}
                          disabled={disabled}
                          onChange={(e) => handleExpirationDateChange(item, e.target.value)}
                        />
                      </DocDropdownRow>
                    ))}
                  </DocDropdownContent>
                )}
              </ChecklistRowItemWrapper>
            )
          })}
        </ChecklistContainer>
      </div>

      {(pendingCount > 0 || expiredCount > 0) && (
        <WarningBanner>
          ⚠️ {pendingCount > 0 ? `${pendingCount} documento(s) pendente(s)` : ''}
          {pendingCount > 0 && expiredCount > 0 ? ' e ' : ''}
          {expiredCount > 0 ? `${expiredCount} documento(s) vencido(s)` : ''} — o RH é notificado até a regularização.
        </WarningBanner>
      )}

      {/* BLOCO 2: DOCUMENTOS ANEXADOS (ANEXOS GERAIS) */}
      <div>
        <SectionHeader>
          DOCUMENTOS ANEXADOS
          <Info size={14} color="#9CA3AF" />
        </SectionHeader>

        <UserAttachmentSection
          attachments={generalAttachments}
          setAttachments={setGeneralAttachments}
          disabled={disabled}
        />
      </div>

      <AttachmentSourceModal
        isOpen={isSourceChoiceOpen}
        onClose={() => {
          setIsSourceChoiceOpen(false)
          setActiveUploadKey(null)
        }}
        title="Adicionar Documento ao Checklist"
        description="Escolha como deseja adicionar o documento:"
        onChooseUpload={() => {
          checklistFileInputRef.current?.click()
        }}
        onChooseGallery={() => {
          setIsGalleryOpen(true)
        }}
      />

      <AttachmentGalleryModal
        isOpen={isGalleryOpen}
        onClose={() => {
          setIsGalleryOpen(false)
          setActiveUploadKey(null)
        }}
        multiple={true}
        title="Galeria da Empresa - Documentos"
        onSelect={(files: GalleryFile[]) => {
          handleSelectGalleryFilesForChecklist(files)
        }}
      />
    </TabWrapper>
  )
}

export default UserDocumentsTab
